/**
 * SKINER Pack Builder
 * Handles pack size selection, slot variant picking, and cart submission.
 * The order holds one pack line with the pieces as _slot_* notes; the bridge
 * (ops/intigo-bridge/src/pack_stock.ts) lowers the pieces' stock from those notes.
 */

const FALLBACK_COLOUR = { hex: '#D8D0C4', light: true };

function isColorOption(name) {
  const n = name.toLowerCase();
  return !n.includes('taille') && !n.includes('size') && !n.includes('pointure');
}

class SKINERPackBuilder {
  constructor(container) {
    this.container = container;
    this.packType = container.dataset.packType;
    this.productData = JSON.parse(container.querySelector('.spb-product-data').textContent);
    this.packConfig = JSON.parse(container.querySelector('.spb-pack-config').textContent);
    const coloursEl = container.querySelector('.spb-colours');
    this.colours = coloursEl ? JSON.parse(coloursEl.textContent) : {};
    this.selectedPackIndex = 0;
    this.slotSelections = [];

    this._renderChips();
    const largestIdx = this.packConfig.reduce((maxI, pack, i, arr) =>
      pack.size > arr[maxI].size ? i : maxI, 0);
    this._selectPack(largestIdx);
    this._bindEvents();
  }

  // ─── Rendering ────────────────────────────────────────────────────────────

  _renderChips() {
    const chipsEl = this.container.querySelector('.spb-chips');
    chipsEl.innerHTML = '';
    this.packConfig.forEach((pack, i) => {
      const btn = document.createElement('button');
      btn.className = 'spb-chip';
      btn.type = 'button';
      btn.dataset.packIndex = i;
      const chipLabel = pack.label.replace(/^Pack\s+(\d+)$/i, '$1 pièces');
      btn.innerHTML = `
        <span class="spb-chip-label">${chipLabel}</span>
        <span class="spb-chip-price">${pack.price} TND</span>
      `;
      chipsEl.appendChild(btn);
    });
  }

  _renderSlots() {
    const pack = this.packConfig[this.selectedPackIndex];
    const slotsSection = this.container.querySelector('.spb-slots-section');
    const slotsGrid = this.container.querySelector('.spb-slots-grid');
    slotsSection.hidden = false;
    slotsGrid.innerHTML = '';
    this.slotSelections = Array.from({ length: pack.size }, () => ({}));

    // Column headers — rendered once, outside the slot rows so they don't affect slot layout
    let colHeaders = slotsSection.querySelector('.spb-col-headers');
    if (!colHeaders) {
      colHeaders = document.createElement('div');
      colHeaders.className = 'spb-col-headers';
      slotsSection.insertBefore(colHeaders, slotsGrid);
    }
    colHeaders.innerHTML = '';
    const numSpacer = document.createElement('span');
    numSpacer.className = 'spb-col-header-spacer';
    colHeaders.appendChild(numSpacer);
    this.productData.options.forEach((optionName) => {
      const lbl = document.createElement('span');
      lbl.className = isColorOption(optionName)
        ? 'spb-col-header-label spb-col-header-label--color'
        : 'spb-col-header-label spb-col-header-label--size';
      lbl.textContent = isColorOption(optionName) ? 'Couleur' : 'Taille';
      colHeaders.appendChild(lbl);
    });

    for (let i = 0; i < pack.size; i++) {
      const slot = document.createElement('div');
      slot.className = 'spb-slot';
      slot.dataset.slotIndex = i;

      const header = document.createElement('p');
      header.className = 'spb-slot-header';
      header.textContent = `${i + 1}`;
      slot.appendChild(header);

      const optionsWrapper = document.createElement('div');
      optionsWrapper.className = 'spb-slot-options';

      this.productData.options.forEach((optionName, optIdx) => {
        const values = this._getOptionValues(optIdx);

        if (isColorOption(optionName)) {
          const swatchCol = document.createElement('div');
          swatchCol.className = 'spb-option-col spb-option-col--color';

          const swatchGroup = document.createElement('div');
          swatchGroup.className = 'spb-swatch-group';
          swatchGroup.dataset.slotIndex = i;
          swatchGroup.dataset.optionIndex = optIdx;

          values.forEach((val) => {
            const { hex, light: isLight } = this.colours[val.toLowerCase()] || FALLBACK_COLOUR;
            const avail = this._isValueAvailable(optIdx, val, i);
            const swatch = document.createElement('button');
            swatch.type = 'button';
            swatch.className = 'spb-swatch'
              + (isLight ? ' spb-swatch--light' : '')
              + (!avail ? ' spb-swatch--unavailable' : '');
            swatch.dataset.value = val;
            swatch.title = avail ? val : `${val} — indisponible`;
            swatch.style.backgroundColor = hex;
            swatch.setAttribute('aria-label', avail ? val : `${val} indisponible`);
            swatch.setAttribute('aria-pressed', 'false');
            swatchGroup.appendChild(swatch);
          });

          swatchCol.appendChild(swatchGroup);
          optionsWrapper.appendChild(swatchCol);
        } else {
          const pillCol = document.createElement('div');
          pillCol.className = 'spb-option-col spb-option-col--size';

          const pillGroup = document.createElement('div');
          pillGroup.className = 'spb-pill-group';
          pillGroup.dataset.slotIndex = i;
          pillGroup.dataset.optionIndex = optIdx;

          values.forEach((val) => {
            const avail = this._isValueAvailable(optIdx, val, i);
            const pill = document.createElement('button');
            pill.type = 'button';
            pill.className = 'spb-pill' + (!avail ? ' spb-pill--unavailable' : '');
            pill.dataset.value = val;
            pill.title = avail ? '' : 'Indisponible';
            pill.setAttribute('aria-pressed', 'false');
            pill.textContent = val;
            pillGroup.appendChild(pill);
          });

          pillCol.appendChild(pillGroup);
          optionsWrapper.appendChild(pillCol);
        }
      });

      slot.appendChild(optionsWrapper);
      slotsGrid.appendChild(slot);
    }

    this._autoPreselectSingleOptions();
    requestAnimationFrame(() => this._syncHeaderWidths());
  }

  _syncHeaderWidths() {
    const colorCol = this.container.querySelector('.spb-option-col--color');
    const headerColorLabel = this.container.querySelector('.spb-col-header-label--color');
    if (colorCol && headerColorLabel) {
      headerColorLabel.style.minWidth = colorCol.offsetWidth + 'px';
    }
  }

  _autoPreselectSingleOptions() {
    const pack = this.packConfig[this.selectedPackIndex];
    for (let slotIdx = 0; slotIdx < pack.size; slotIdx++) {
      this.productData.options.forEach((optionName, optIdx) => {
        const values = this._getOptionValues(optIdx);
        const available = values.filter((v) => this._isValueAvailable(optIdx, v, slotIdx));
        if (available.length !== 1) return;
        const slotEl = this.container.querySelectorAll('.spb-slot')[slotIdx];
        if (isColorOption(optionName)) {
          const group = slotEl.querySelector(`.spb-swatch-group[data-option-index="${optIdx}"]`);
          const swatch = group?.querySelector(`[data-value="${available[0]}"]`);
          if (swatch) this._onSwatchClick(swatch, group);
        } else {
          const group = slotEl.querySelector(`.spb-pill-group[data-option-index="${optIdx}"]`);
          const pill = group?.querySelector(`[data-value="${available[0]}"]`);
          if (pill) this._onPillClick(pill, group);
        }
      });
    }
  }

  _getOptionValues(optionIndex) {
    const seen = new Set();
    const values = [];
    this.productData.variants.forEach((v) => {
      const val = v[`option${optionIndex + 1}`];
      if (val && !seen.has(val)) {
        seen.add(val);
        values.push(val);
      }
    });
    return values;
  }

  // ─── Availability ─────────────────────────────────────────────────────────

  _variantInStock(v) {
    // Shopify's own answer, the same one the single piece's page follows: a piece
    // on "keep selling when out of stock" stays buyable at 0.
    return v.available;
  }

  _isValueAvailable(optionIndex, value, slotIdx) {
    const currentSel = this.slotSelections[slotIdx] || {};
    return this.productData.variants.some((v) => {
      if (!this._variantInStock(v)) return false;
      if (v[`option${optionIndex + 1}`] !== value) return false;
      return this.productData.options.every((_, i) => {
        if (i === optionIndex) return true;
        const selVal = currentSel[`option${i}`];
        if (!selVal) return true;
        return v[`option${i + 1}`] === selVal;
      });
    });
  }

  _updateSlotOptionAvailability(slotIdx) {
    const slotEl = this.container.querySelectorAll('.spb-slot')[slotIdx];
    if (!slotEl) return;

    slotEl.querySelectorAll('.spb-swatch-group').forEach((group) => {
      const optIdx = parseInt(group.dataset.optionIndex, 10);
      group.querySelectorAll('.spb-swatch').forEach((swatch) => {
        const avail = this._isValueAvailable(optIdx, swatch.dataset.value, slotIdx);
        swatch.classList.toggle('spb-swatch--unavailable', !avail);
        swatch.title = avail
          ? swatch.dataset.value
          : `${swatch.dataset.value} — indisponible`;
      });
    });

    slotEl.querySelectorAll('.spb-pill-group').forEach((group) => {
      const optIdx = parseInt(group.dataset.optionIndex, 10);
      group.querySelectorAll('.spb-pill').forEach((pill) => {
        const avail = this._isValueAvailable(optIdx, pill.dataset.value, slotIdx);
        pill.classList.toggle('spb-pill--unavailable', !avail);
        pill.title = avail ? '' : 'Indisponible';
      });
    });
  }

  // ─── Selection Logic ──────────────────────────────────────────────────────

  _selectPack(index) {
    this.selectedPackIndex = index;
    this.container.querySelectorAll('.spb-chip').forEach((btn, i) => {
      btn.classList.toggle('spb-chip--active', i === index);
      btn.setAttribute('aria-pressed', i === index);
    });
    this._renderSlots();
    this._updateButton();
  }

  _onSwatchClick(btn, group) {
    const slotIdx = parseInt(group.dataset.slotIndex, 10);
    const optIdx = parseInt(group.dataset.optionIndex, 10);
    const val = btn.dataset.value;
    const isActive = btn.classList.contains('spb-swatch--active');

    group.querySelectorAll('.spb-swatch').forEach((s) => {
      s.classList.remove('spb-swatch--active');
      s.setAttribute('aria-pressed', 'false');
    });

    if (!isActive) {
      btn.classList.add('spb-swatch--active');
      btn.setAttribute('aria-pressed', 'true');
      this.slotSelections[slotIdx][`option${optIdx}`] = val;
    } else {
      delete this.slotSelections[slotIdx][`option${optIdx}`];
    }

    this._updateSlotOptionAvailability(slotIdx);
    this._updateSlotState(slotIdx);
    this._updateButton();
  }

  _onPillClick(btn, group) {
    const slotIdx = parseInt(group.dataset.slotIndex, 10);
    const optIdx = parseInt(group.dataset.optionIndex, 10);
    const val = btn.dataset.value;
    const isActive = btn.classList.contains('spb-pill--active');

    group.querySelectorAll('.spb-pill').forEach((p) => {
      p.classList.remove('spb-pill--active');
      p.setAttribute('aria-pressed', 'false');
    });

    if (!isActive) {
      btn.classList.add('spb-pill--active');
      btn.setAttribute('aria-pressed', 'true');
      this.slotSelections[slotIdx][`option${optIdx}`] = val;
    } else {
      delete this.slotSelections[slotIdx][`option${optIdx}`];
    }

    this._updateSlotOptionAvailability(slotIdx);
    this._updateSlotState(slotIdx);
    this._updateButton();
  }

  _updateSlotState(slotIdx) {
    const slotEl = this.container.querySelectorAll('.spb-slot')[slotIdx];
    if (!slotEl) return;
    const filled = !!this._resolveVariantId(slotIdx);
    slotEl.classList.toggle('spb-slot--filled', filled);
  }

  _shakeEl(el) {
    el.classList.remove('spb-el--shake');
    void el.offsetWidth; // force reflow to restart animation
    el.classList.add('spb-el--shake');
    el.addEventListener('animationend', () => el.classList.remove('spb-el--shake'), { once: true });
  }

  _resolveVariantId(slotIdx) {
    const sel = this.slotSelections[slotIdx];
    const allFilled = this.productData.options.every((_, i) => !!sel[`option${i}`]);
    if (!allFilled) return null;
    const match = this.productData.variants.find((v) =>
      this.productData.options.every((_, i) => v[`option${i + 1}`] === sel[`option${i}`])
    );
    return match?.id ?? null;
  }

  _allSlotsFilled() {
    const pack = this.packConfig[this.selectedPackIndex];
    for (let i = 0; i < pack.size; i++) {
      if (!this._resolveVariantId(i)) return false;
    }
    return true;
  }

  _updateButton() {
    const btn = this.container.querySelector('.spb-atc');
    const ready = this._allSlotsFilled();
    btn.disabled = !ready;
    btn.classList.toggle('spb-atc--ready', ready);
  }

  // ─── Cart ─────────────────────────────────────────────────────────────────

  async _addToCart() {
    const btn = this.container.querySelector('.spb-atc');
    const pack = this.packConfig[this.selectedPackIndex];

    btn.disabled = true;
    btn.textContent = 'Ajout en cours…';

    try {
      if (pack.size === 1) {
        const variantId = this._resolveVariantId(0);
        await this._postToCart({ id: variantId, quantity: 1 });
      } else {
        const properties = {
          _skiner_pack: 'true',
          _pack_size: String(pack.size),
          _pack_type: this.packType,
        };

        const slot0VariantId = this._resolveVariantId(0);
        const slot0Variant = this.productData.variants.find((v) => v.id === slot0VariantId);
        if (slot0Variant?.image) properties['_slot_1_img'] = slot0Variant.image;

        for (let i = 0; i < pack.size; i++) {
          const variantId = this._resolveVariantId(i);
          properties[`_slot_${i + 1}_variant_id`] = String(variantId);

          const slotEl = this.container.querySelectorAll('.spb-slot')[i];
          const selected = [];
          slotEl.querySelectorAll('.spb-swatch--active, .spb-pill--active').forEach((el) => {
            selected.push(el.dataset.value);
          });
          properties[`_slot_${i + 1}_label`] = selected.filter(Boolean).join(' / ');
        }

        await this._postToCart({ id: pack.variantId, quantity: 1, properties });
      }

      fetch('/?sections=cart-icon-bubble')
        .then((r) => r.json())
        .then((data) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data['cart-icon-bubble'], 'text/html');
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) bubble.innerHTML = doc.querySelector('#cart-icon-bubble')?.innerHTML ?? '';
        })
        .catch(() => {});

      btn.textContent = 'Ajouté !';
      setTimeout(() => {
        window.location.href = '/cart';
      }, 500);
    } catch (err) {
      console.error('[SKINER Pack Builder]', err);
      btn.textContent = 'Erreur — réessayer';
      btn.disabled = false;
    }
  }

  async _postToCart(payload) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Cart error');
    }
    return res.json();
  }

  // ─── Events ───────────────────────────────────────────────────────────────

  _bindEvents() {
    this.container.addEventListener('click', (e) => {
      const chip = e.target.closest('.spb-chip');
      if (chip) {
        this._selectPack(parseInt(chip.dataset.packIndex, 10));
        return;
      }

      const swatch = e.target.closest('.spb-swatch');
      if (swatch) {
        if (swatch.classList.contains('spb-swatch--unavailable')) {
          this._shakeEl(swatch);
          return;
        }
        const group = swatch.closest('.spb-swatch-group');
        if (group) this._onSwatchClick(swatch, group);
        return;
      }

      const pill = e.target.closest('.spb-pill');
      if (pill) {
        if (pill.classList.contains('spb-pill--unavailable')) {
          this._shakeEl(pill);
          return;
        }
        const group = pill.closest('.spb-pill-group');
        if (group) this._onPillClick(pill, group);
        return;
      }
    });

    this.container
      .querySelector('.spb-atc')
      .addEventListener('click', () => this._addToCart());
  }
}

// ─── Init ────────────────────────────────────────────────────────────────────

document.querySelectorAll('.skiner-pack-builder').forEach((el) => {
  new SKINERPackBuilder(el);
});
